import { Box, Grid, Typography } from "@mui/material"
import { SolutionField } from "./SolutionField"
import { useFormikContext } from "formik"
import { ISolution } from "../models/Solution"
import { LabellessStyledNumberMuiField } from "./muiFields/StyledNumberMuiField"
import { StyledCheckBox } from "./Checkbox"
import useWindowSize from "../windowSize"
import { ReactElement, useState } from "react"
import { ProductType } from "../models/Product"
import { areAllCornersSame } from "../models/Customization"
import { GlassOrder } from "../models/Layers"
import { StyledMuiFieldLabel } from "./muiFields/MuiFIeldLabel"

export function CornerRadiusField(props: {
  productType: ProductType
}): ReactElement {
  const { values, setFieldValue } = useFormikContext<ISolution>()
  const [independentLayers, setInd] = useState<boolean>(
    props.productType === ProductType.FortouchO &&
      values.customization.independentCornes
  )

  return (
    <>
      {independentLayers && (
        <StyledMuiFieldLabel label={"Front glass"} paired={true} />
      )}
      <CornerRadiusFieldLayer layer={GlassOrder.Front} />

      {independentLayers && (
        <>
          <StyledMuiFieldLabel label={"Middle glass"} paired={true} />
          <CornerRadiusFieldLayer layer={GlassOrder.Middle} />
          <StyledMuiFieldLabel label={"Rear glass"} paired={true} />
          <CornerRadiusFieldLayer layer={GlassOrder.Rear} />
        </>
      )}

      {props.productType === ProductType.FortouchO && (
        <StyledCheckBox
          name="Independent layers"
          checked={independentLayers}
          onChange={() => {
            setFieldValue("customization.independentCornes", !independentLayers)
            setInd(!independentLayers)
          }}
        />
      )}
    </>
  )
}

function CornerRadiusFieldLayer(props: { layer: GlassOrder }): ReactElement {
  const { values, setFieldValue } = useFormikContext<ISolution>()
  const [allCornersSame, setAllCornersSame] = useState<boolean>(
    areAllCornersSame(values.customization.corners[props.layer])
  )
  const size = useWindowSize()
  const direction = size.width < 1600 && size.width > 1100 ? "column" : "row"
  const sx_ =
    size.width < 1600 && size.width > 1100
      ? {
          marginBottom: "15px",
        }
      : {}

  return (
    <Grid container direction={direction}>
      <Grid item xs={12} md={5} sx={{ ...sx_ }}>
        <Typography
          paddingRight="20px"
          color="#250048"
          fontSize="100%"
          maxWidth="200px"
        >
          Corner radius (mm)
        </Typography>
      </Grid>
      <Grid item xs={12} md={7}>
        <CornerRadiusInput
          allCornersSame={allCornersSame}
          layer={props.layer}
        />
        <StyledCheckBox
          name="Independent corners"
          checked={allCornersSame}
          onChange={() => {
            setFieldValue(
              `customization.corners.${props.layer}.tr`,
              values.customization.corners[props.layer].tl
            )
            setFieldValue(
              `customization.corners.${props.layer}.bl`,
              values.customization.corners[props.layer].tl
            )
            setFieldValue(
              `customization.corners.${props.layer}.br`,
              values.customization.corners[props.layer].tl
            )
            setAllCornersSame(!allCornersSame)
          }}
        />
      </Grid>
    </Grid>
  )
}

function CornerRadiusInput(props: {
  allCornersSame: boolean
  layer: GlassOrder
}): ReactElement {
  const { values, setFieldValue } = useFormikContext<ISolution>()

  if (props.allCornersSame === true)
    return (
      <LabellessStyledNumberMuiField
        integer={false}
        name={`customization.corners[${props.layer}].tr`}
        disabled={false}
        defaultValue={values.customization.corners[props.layer].tr}
        onChange={(event) => {
          setFieldValue(
            `customization.corners.${props.layer}.tl`,
            event.target.value
          )
          setFieldValue(
            `customization.corners.${props.layer}.tr`,
            event.target.value
          )
          setFieldValue(
            `customization.corners.${props.layer}.bl`,
            event.target.value
          )
          setFieldValue(
            `customization.corners.${props.layer}.br`,
            event.target.value
          )
        }}
      />
    )
  else return <CornerIndependent layer={props.layer} />
}

function CornerIndependent(props: { layer: GlassOrder }): ReactElement {
  return (
    <Box>
      <Grid container alignItems="center">
        <Grid>
          <Box
            sx={{
              marginRight: "15px",
              height: "15px",
              width: "15px",
              borderRadius: "5px 0 0 0",
              borderTop: "2px solid #250048",
              borderLeft: "2px solid #250048",
            }}
          />
        </Grid>
        <Grid>
          <SolutionField name={`customization.corners.${props.layer}.tl`} />
        </Grid>
        <Grid>
          <SolutionField name={`customization.corners.${props.layer}.tr`} />
        </Grid>
        <Grid>
          <Box
            sx={{
              height: "15px",
              width: "15px",
              borderRadius: "0 5px 0 0",
              borderTop: "2px solid #250048",
              borderRight: "2px solid #250048",
            }}
          />
        </Grid>
      </Grid>

      <Grid container alignItems="center" paddingTop="15px">
        <Grid>
          <Box
            sx={{
              marginRight: "15px",
              height: "15px",
              width: "15px",
              borderRadius: "0 0 0 5px",
              borderBottom: "2px solid #250048",
              borderLeft: "2px solid #250048",
            }}
          />
        </Grid>
        <Grid>
          <SolutionField name={`customization.corners.${props.layer}.bl`} />
        </Grid>
        <Grid>
          <SolutionField name={`customization.corners.${props.layer}.br`} />
        </Grid>
        <Grid>
          <Box
            sx={{
              height: "15px",
              width: "15px",
              borderRadius: "0 0 5px 0",
              borderBottom: "2px solid #250048",
              borderRight: "2px solid #250048",
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
